'use client';

import { useEffect, useState } from 'react';
import { toApiKey } from '@polymarket/bindings';
import { createSecureClient, remoteBuilderSigning, type SecureClient } from '@polymarket/client';
import { signerFrom } from '@polymarket/client/viem';

import { useAuth } from '@/hooks/useAuth';
import { usePolymarketClient } from '@/hooks/usePolymarketClient';
import { normalizeClobAddress } from '@/utils/clob.utils';

import { API_BASE_URL, API_ENDPOINTS } from '@/constants';

/**
 * useComboClient
 *
 * A SecureClient configured for the NON-CUSTODIAL combo (RFQ) flow. It is built
 * exactly like useClobClient — the user's own Privy-backed wallet is the order
 * signer and their deposit wallet is the account/funder — plus builder auth via
 * `remoteBuilderSigning`. Combo create/accept require BOTH account auth (the
 * user's signer, handled by the SDK) AND builder auth (the builder secret, which
 * stays on our server behind /combos/builder/sign). This keeps custody with the
 * user: the backend never signs for the user's deposit wallet.
 *
 * @returns A SecureClient, or null while auth/setup is incomplete.
 */

const BUILDER_SIGN_URL = `${API_BASE_URL}${API_ENDPOINTS.COMBOS.BUILDER_SIGN}`;

// Share one SecureClient across callers (its creation performs network auth).
// Keyed by the inputs that define client identity — including the access token,
// since it's embedded in the builder-signing request headers.
let cachedKey: string | null = null;
let cachedClientPromise: Promise<SecureClient> | null = null;

function getSharedComboClient(
  walletClient: any,
  proxyWalletAddress: string,
  creds: { apiKey: string; secret: string; passphrase: string },
  accessToken: string,
): Promise<SecureClient> {
  const signerAddress = walletClient?.account?.address ?? '';
  const key = `${normalizeClobAddress(proxyWalletAddress)}:${creds.apiKey}:${signerAddress}:${accessToken}`;
  if (cachedKey === key && cachedClientPromise) {
    return cachedClientPromise;
  }
  cachedKey = key;
  cachedClientPromise = createSecureClient({
    signer: signerFrom(walletClient),
    wallet: normalizeClobAddress(proxyWalletAddress),
    credentials: {
      key: toApiKey(creds.apiKey),
      secret: creds.secret,
      passphrase: creds.passphrase,
    },
    apiKey: remoteBuilderSigning({
      url: BUILDER_SIGN_URL,
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
  });
  return cachedClientPromise;
}

export function useComboClient(): SecureClient | null {
  const { user, accessToken } = useAuth();
  const { signer } = usePolymarketClient();

  const proxyWalletAddress = user?.proxyWalletAddress;
  const apiKeyCreds = user?.clobCredentials;

  const [client, setClient] = useState<SecureClient | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!accessToken || !proxyWalletAddress || !signer || !apiKeyCreds) {

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setClient(null);
      return;
    }

    getSharedComboClient(
      signer,
      proxyWalletAddress,
      {
        apiKey: apiKeyCreds.apiKey,
        secret: apiKeyCreds.secret,
        passphrase: apiKeyCreds.passphrase,
      },
      accessToken,
    )
      .then((c) => {
        if (!cancelled) setClient(c);
      })
      .catch((err) => {
        console.error('[useComboClient] Failed to create SecureClient:', err);
        if (!cancelled) setClient(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    proxyWalletAddress,
    signer,
    apiKeyCreds?.apiKey,
    apiKeyCreds?.secret,
    apiKeyCreds?.passphrase,
  ]);

  return client;
}
